import { useState, useEffect } from 'react';
import { Star, Users, Download, RefreshCw, Loader2, Mail, Calendar, Lock, LogOut, FileText, Plus, Trash2, TrendingUp, FileSignature, ArrowRightLeft, UserMinus, Rss, Zap, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useSeo } from '../components/custom/Seo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RosterAdmin } from '../components/admin/RosterAdmin';
import { TeamHubAdmin } from '../components/admin/TeamHubAdmin';
import axios from 'axios';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  useSeo({
    title: 'Admin | DC Sports Report',
    description: 'DC Sports Report administration.',
    path: '/admin',
    noindex: true,
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncingMailchimp, setSyncingMailchimp] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  
  // RSS Feed state
  const [feedStatus, setFeedStatus] = useState(null);
  const [fetchingNews, setFetchingNews] = useState(false);
  const [fetchMessage, setFetchMessage] = useState({ type: '', text: '' });
  
  // Article form state
  const [articleForm, setArticleForm] = useState({
    team: '',
    title: '',
    author: '',
    summary: '',
    content: '',
    image_data_url: ''
  });
  const [articleImagePreview, setArticleImagePreview] = useState('');
  const [articleImageError, setArticleImageError] = useState('');
  const [articleSubmitting, setArticleSubmitting] = useState(false);
  const [articleMessage, setArticleMessage] = useState({ type: '', text: '' });

  // Free Agency Deal form state
  const [dealForm, setDealForm] = useState({
    player_name: '',
    position: '',
    deal_type: '',
    details: '',
    contract_value: '',
    contract_years: ''
  });
  const [dealSubmitting, setDealSubmitting] = useState(false);
  const [dealMessage, setDealMessage] = useState({ type: '', text: '' });

  // Scoreboard state (for Defenders manual entry - only UFL needs manual)
  const [manualScores, setManualScores] = useState({ defenders: [] });
  const [scoreForm, setScoreForm] = useState({
    team: 'defenders',
    home_team: '',
    away_team: '',
    home_score: '',
    away_score: '',
    status: 'Scheduled',
    game_date: '',
    game_time: '',
    is_home: true
  });
  const [scoreSubmitting, setScoreSubmitting] = useState(false);
  const [scoreMessage, setScoreMessage] = useState({ type: '', text: '' });
  const [authToken, setAuthToken] = useState(null);

  // Check if already logged in using secure token in memory only
  useEffect(() => {
    // Auth state is kept in memory only - more secure than sessionStorage
    // User will need to re-login on page refresh, but this prevents XSS attacks
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success && response.data.token) {
        // Keep token in memory only; attach to subsequent admin API requests
        setAuthToken(response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        setIsAuthenticated(true);
      } else {
        setLoginError('Invalid password. Please try again.');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        setLoginError('Too many failed attempts. Please try again in 15 minutes.');
      } else {
        setLoginError('Something went wrong. Please try again.');
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setAuthToken(null);
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setPassword('');
  };

  const fetchSubscribers = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const response = await axios.get(`${API}/newsletter/subscribers`);
      setSubscribers(response.data.subscribers || []);
    } catch (error) {
      // Error - silent fail for UX
      logError(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      setArticles(response.data.articles || []);
    } catch (error) {
      // Error - silent fail for UX
      logError(error);
    }
  };

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API}/freeagency`);
      setDeals(response.data.deals || []);
    } catch (error) {
      // Error - silent fail for UX
      logError(error);
    }
  };

  const fetchFeedStatus = async () => {
    try {
      const response = await axios.get(`${API}/freeagency/feed-status`);
      setFeedStatus(response.data);
    } catch (error) {
      // Error - silent fail for UX
      logError(error);
    }
  };

  const fetchManualScores = async () => {
    try {
      const defResponse = await axios.get(`${API}/scores/manual/defenders`);
      setManualScores({
        defenders: defResponse.data.scores || []
      });
    } catch (error) {
      // Error - silent fail for UX
      logError(error);
    }
  };

  const handleScoreSubmit = async (e) => {
    e.preventDefault();
    
    if (!scoreForm.home_team || !scoreForm.away_team || !scoreForm.game_date) {
      setScoreMessage({ type: 'error', text: 'Please fill in required fields (teams and date)' });
      return;
    }

    setScoreSubmitting(true);
    setScoreMessage({ type: '', text: '' });

    try {
      const payload = {
        ...scoreForm,
        home_score: scoreForm.home_score ? parseInt(scoreForm.home_score) : null,
        away_score: scoreForm.away_score ? parseInt(scoreForm.away_score) : null,
      };
      await axios.post(`${API}/scores/manual`, payload);
      setScoreMessage({ type: 'success', text: 'Game added successfully!' });
      setScoreForm({
        team: 'defenders',
        home_team: '',
        away_team: '',
        home_score: '',
        away_score: '',
        status: 'Scheduled',
        game_date: '',
        game_time: '',
        is_home: true
      });
      fetchManualScores();
    } catch (error) {
      setScoreMessage({ type: 'error', text: 'Failed to add game. Please try again.' });
    } finally {
      setScoreSubmitting(false);
    }
  };

  const handleDeleteScore = async (scoreId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;
    
    try {
      await axios.delete(`${API}/scores/manual/${scoreId}`);
      fetchManualScores();
    } catch (error) {
      // Error - silent fail for UX
      logError(error);
    }
  };

  const handleFetchNews = async () => {
    setFetchingNews(true);
    setFetchMessage({ type: '', text: '' });
    
    try {
      const response = await axios.post(`${API}/freeagency/fetch-news`);
      setFetchMessage({ 
        type: 'success', 
        text: response.data.message 
      });
      // Refresh deals list after fetching
      fetchDeals();
      fetchFeedStatus();
    } catch (error) {
      setFetchMessage({ 
        type: 'error', 
        text: 'Failed to fetch news. Please try again.' 
      });
    } finally {
      setFetchingNews(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscribers();
      fetchArticles();
      fetchDeals();
      fetchFeedStatus();
      fetchManualScores();
    }
  }, [isAuthenticated]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const exportToCSV = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'Subscribed At'];
    const csvData = subscribers.map(sub => [
      sub.first_name || '',
      sub.last_name || '',
      sub.email,
      sub.subscribed_at
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `dc_sports_report_subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const syncToMailchimp = async () => {
    setSyncingMailchimp(true);
    setSyncMessage(null);
    try {
      const res = await axios.post(`${API}/newsletter/sync-mailchimp`);
      const { total, synced, failed } = res.data;
      setSyncMessage({
        type: failed.length ? 'warn' : 'success',
        text: `Synced ${synced} of ${total} subscribers to Mailchimp.${failed.length ? ` Skipped: ${failed.join(', ')}` : ''}`,
      });
    } catch (error) {
      setSyncMessage({ type: 'error', text: 'Mailchimp sync failed. Check the backend logs.' });
    } finally {
      setSyncingMailchimp(false);
    }
  };

  const handleArticleSubmit = async (e) => {
    e.preventDefault();
    
    if (!articleForm.team || !articleForm.title || !articleForm.author || !articleForm.content) {
      setArticleMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    setArticleSubmitting(true);
    setArticleMessage({ type: '', text: '' });

    try {
      await axios.post(`${API}/articles`, articleForm);
      setArticleMessage({ type: 'success', text: 'Article published successfully!' });
      setArticleForm({ team: '', title: '', author: '', summary: '', content: '', image_data_url: '' });
      setArticleImagePreview('');
      setArticleImageError('');
      fetchArticles();
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Failed to publish article. Please try again.';
      setArticleMessage({ type: 'error', text: detail });
    } finally {
      setArticleSubmitting(false);
    }
  };

  const handleArticleImageChange = (e) => {
    setArticleImageError('');
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setArticleForm((prev) => ({ ...prev, image_data_url: '' }));
      setArticleImagePreview('');
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setArticleImageError('Image must be JPG, PNG, WEBP, or GIF.');
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      setArticleImageError('Image must be under 6 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setArticleForm((prev) => ({ ...prev, image_data_url: dataUrl }));
      setArticleImagePreview(dataUrl);
    };
    reader.onerror = () => setArticleImageError('Could not read file.');
    reader.readAsDataURL(file);
  };

  const clearArticleImage = () => {
    setArticleForm((prev) => ({ ...prev, image_data_url: '' }));
    setArticleImagePreview('');
    setArticleImageError('');
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      await axios.delete(`${API}/articles/${articleId}`);
      fetchArticles();
    } catch (error) {
      // Error - silent fail for UX
      logError(error);
    }
  };

  // Free Agency Deal handlers
  const handleDealSubmit = async (e) => {
    e.preventDefault();
    
    if (!dealForm.player_name || !dealForm.position || !dealForm.deal_type || !dealForm.details) {
      setDealMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    setDealSubmitting(true);
    setDealMessage({ type: '', text: '' });

    try {
      await axios.post(`${API}/freeagency`, dealForm);
      setDealMessage({ type: 'success', text: 'Deal added successfully!' });
      setDealForm({ player_name: '', position: '', deal_type: '', details: '', contract_value: '', contract_years: '' });
      fetchDeals();
    } catch (error) {
      setDealMessage({ type: 'error', text: 'Failed to add deal. Please try again.' });
    } finally {
      setDealSubmitting(false);
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm('Are you sure you want to delete this deal?')) return;
    
    try {
      await axios.delete(`${API}/freeagency/${dealId}`);
      fetchDeals();
    } catch (error) {
      // Error - silent fail for UX
      logError(error);
    }
  };

  const dealTypeOptions = [
    { value: 'signed', label: 'Signed', color: '#22c55e' },
    { value: 'traded', label: 'Traded', color: '#3b82f6' },
    { value: 'released', label: 'Released', color: '#ef4444' },
    { value: 'extended', label: 'Extended', color: '#a855f7' },
    { value: 'restructured', label: 'Restructured', color: '#f59e0b' },
  ];

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div data-testid="admin-login" className="min-h-screen bg-[#002868] flex items-center justify-center px-4">
        <Card className="w-full max-w-md border-2 border-white shadow-brutal-lg">
          <CardHeader className="bg-[#BF0A30] text-white text-center">
            <div className="w-16 h-16 bg-white mx-auto mb-4 flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#002868]" />
            </div>
            <CardTitle className="font-heading text-2xl uppercase tracking-wide">
              Admin Access
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-14 border-2 border-black text-lg"
                  disabled={loginLoading}
                />
              </div>
              {loginError && (
                <p className="text-red-600 text-sm font-medium">{loginError}</p>
              )}
              <Button
                type="submit"
                disabled={loginLoading || !password}
                className="w-full h-14 bg-[#002868] hover:bg-[#001a42] text-white font-bold uppercase tracking-wide border-2 border-black shadow-brutal"
              >
                {loginLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Enter'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-slate-100 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="font-heading text-4xl text-[#002868] uppercase tracking-tight mb-2">
              Admin Dashboard
            </h1>
            <p className="text-slate-600">
              Manage your DC Sports Report content and subscribers
            </p>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-2 border-slate-300 hover:border-red-500 hover:text-red-500"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="subscribers" className="space-y-6">
          <TabsList className="bg-white border-2 border-black shadow-brutal-sm">
            <TabsTrigger value="subscribers" className="data-[state=active]:bg-[#002868] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Subscribers
            </TabsTrigger>
            <TabsTrigger value="articles" className="data-[state=active]:bg-[#002868] data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              Articles
            </TabsTrigger>
            <TabsTrigger value="new-article" className="data-[state=active]:bg-[#002868] data-[state=active]:text-white">
              <Plus className="w-4 h-4 mr-2" />
              New Article
            </TabsTrigger>
            <TabsTrigger value="freeagency" className="data-[state=active]:bg-[#5A1414] data-[state=active]:text-white">
              <TrendingUp className="w-4 h-4 mr-2" />
              Free Agency
            </TabsTrigger>
            <TabsTrigger value="scoreboard" className="data-[state=active]:bg-[#002868] data-[state=active]:text-white">
              <Trophy className="w-4 h-4 mr-2" />
              Scoreboard
            </TabsTrigger>
            <TabsTrigger value="roadto53" data-testid="admin-tab-roadto53" className="data-[state=active]:bg-[#5A1414] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Road to 53
            </TabsTrigger>
            <TabsTrigger value="teamhub" data-testid="admin-tab-teamhub" className="data-[state=active]:bg-[#002868] data-[state=active]:text-white">
              <Star className="w-4 h-4 mr-2" />
              Team Hub
            </TabsTrigger>
          </TabsList>

          {/* Team Hub Tab */}
          <TabsContent value="teamhub" className="space-y-6">
            <TeamHubAdmin />
          </TabsContent>

          {/* Road to 53 Tab */}
          <TabsContent value="roadto53" className="space-y-6">
            <RosterAdmin />
          </TabsContent>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-2 border-[#002868]">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#002868] flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wide">Total Subscribers</p>
                    <p className="text-3xl font-bold text-[#002868]">{subscribers.length}</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-2 border-[#BF0A30]">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#BF0A30] flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wide">This Week</p>
                    <p className="text-3xl font-bold text-[#BF0A30]">
                      {subscribers.filter(s => {
                        const subDate = new Date(s.subscribed_at);
                        const weekAgo = new Date();
                        weekAgo.setDate(weekAgo.getDate() - 7);
                        return subDate >= weekAgo;
                      }).length}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-[#FFB612]">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#FFB612] flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-[#002868]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 uppercase tracking-wide">Today</p>
                    <p className="text-3xl font-bold text-[#002868]">
                      {subscribers.filter(s => {
                        const subDate = new Date(s.subscribed_at).toDateString();
                        const today = new Date().toDateString();
                        return subDate === today;
                      }).length}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                onClick={() => fetchSubscribers(true)}
                disabled={refreshing}
                className="bg-[#002868] hover:bg-[#001a42] text-white border-2 border-black shadow-brutal-sm"
              >
                {refreshing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh
              </Button>
              
              <Button
                onClick={exportToCSV}
                disabled={subscribers.length === 0}
                className="bg-[#FFB612] hover:bg-[#e5a410] text-[#002868] border-2 border-black shadow-brutal-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>

              <Button
                data-testid="sync-mailchimp-btn"
                onClick={syncToMailchimp}
                disabled={syncingMailchimp || subscribers.length === 0}
                className="bg-[#BF0A30] hover:bg-[#9c0827] text-white border-2 border-black shadow-brutal-sm"
              >
                {syncingMailchimp ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Sync to Mailchimp
              </Button>
            </div>

            {syncMessage && (
              <p data-testid="sync-mailchimp-result" className={`text-sm font-medium ${syncMessage.type === 'error' ? 'text-red-600' : syncMessage.type === 'warn' ? 'text-amber-600' : 'text-green-700'}`}>
                {syncMessage.text}
              </p>
            )}

            {/* Subscribers Table */}
            <Card className="border-2 border-black shadow-brutal">
              <CardHeader className="bg-[#002868] text-white">
                <CardTitle className="font-heading text-xl uppercase tracking-wide">
                  Subscriber List
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-8 h-8 text-[#002868] animate-spin" />
                  </div>
                ) : subscribers.length === 0 ? (
                  <div className="text-center py-16">
                    <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No subscribers yet.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-100">
                        <TableHead className="font-bold text-[#002868]">#</TableHead>
                        <TableHead className="font-bold text-[#002868]">First Name</TableHead>
                        <TableHead className="font-bold text-[#002868]">Last Name</TableHead>
                        <TableHead className="font-bold text-[#002868]">Email</TableHead>
                        <TableHead className="font-bold text-[#002868]">Subscribed At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscribers.map((subscriber, index) => (
                        <TableRow key={subscriber.id} className="hover:bg-slate-50">
                          <TableCell className="font-medium">{index + 1}</TableCell>
                          <TableCell>{subscriber.first_name || '-'}</TableCell>
                          <TableCell>{subscriber.last_name || '-'}</TableCell>
                          <TableCell className="font-medium">{subscriber.email}</TableCell>
                          <TableCell className="text-slate-500">{formatDate(subscriber.subscribed_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Articles Tab */}
          <TabsContent value="articles" className="space-y-6">
            <Card className="border-2 border-black shadow-brutal">
              <CardHeader className="bg-[#002868] text-white">
                <CardTitle className="font-heading text-xl uppercase tracking-wide">
                  Published Articles
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {articles.length === 0 ? (
                  <div className="text-center py-16">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No articles yet. Create your first article!</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-100">
                        <TableHead className="font-bold text-[#002868]">Title</TableHead>
                        <TableHead className="font-bold text-[#002868]">Team</TableHead>
                        <TableHead className="font-bold text-[#002868]">Author</TableHead>
                        <TableHead className="font-bold text-[#002868]">Date</TableHead>
                        <TableHead className="font-bold text-[#002868]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {articles.map((article) => (
                        <TableRow key={article.id} className="hover:bg-slate-50">
                          <TableCell className="font-medium">{article.title}</TableCell>
                          <TableCell className="capitalize">{article.team}</TableCell>
                          <TableCell>{article.author}</TableCell>
                          <TableCell className="text-slate-500">{formatDate(article.created_at)}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteArticle(article.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* New Article Tab */}
          <TabsContent value="new-article" className="space-y-6">
            <Card className="border-2 border-black shadow-brutal">
              <CardHeader className="bg-[#002868] text-white">
                <CardTitle className="font-heading text-xl uppercase tracking-wide">
                  Create New Article
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleArticleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Team *
                      </label>
                      <Select 
                        value={articleForm.team} 
                        onValueChange={(value) => setArticleForm({...articleForm, team: value})}
                      >
                        <SelectTrigger className="h-12 border-2 border-black">
                          <SelectValue placeholder="Select a team" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="commanders">Commanders (NFL)</SelectItem>
                          <SelectItem value="capitals">Capitals (NHL)</SelectItem>
                          <SelectItem value="nationals">Nationals (MLB)</SelectItem>
                          <SelectItem value="wizards">Wizards (NBA)</SelectItem>
                          <SelectItem value="mystics">Mystics (WNBA)</SelectItem>
                          <SelectItem value="dcunited">DC United (MLS)</SelectItem>
                          <SelectItem value="spirit">Spirit (NWSL)</SelectItem>
                          <SelectItem value="defenders">Defenders (UFL)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Author *
                      </label>
                      <Input
                        type="text"
                        placeholder="Author name"
                        value={articleForm.author}
                        onChange={(e) => setArticleForm({...articleForm, author: e.target.value})}
                        className="h-12 border-2 border-black"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Title *
                    </label>
                    <Input
                      type="text"
                      placeholder="Article title"
                      value={articleForm.title}
                      onChange={(e) => setArticleForm({...articleForm, title: e.target.value})}
                      className="h-12 border-2 border-black"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Summary (optional)
                    </label>
                    <Input
                      type="text"
                      placeholder="Brief summary for article preview"
                      value={articleForm.summary}
                      onChange={(e) => setArticleForm({...articleForm, summary: e.target.value})}
                      className="h-12 border-2 border-black"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Featured Image (optional)
                    </label>
                    <p className="text-xs text-slate-500 mb-2">
                      Used as the article thumbnail and as the share preview image on social media. JPG, PNG, WEBP, or GIF, up to 6MB. Recommended 1200x630.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 items-start">
                      <input
                        data-testid="article-image-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleArticleImageChange}
                        className="block w-full text-sm text-slate-700 file:mr-4 file:py-2 file:px-4 file:border-2 file:border-black file:bg-[#FFB612] file:text-[#002868] file:font-bold file:uppercase file:tracking-wide file:cursor-pointer hover:file:bg-[#e5a410]"
                      />
                      {articleImagePreview && (
                        <div className="flex items-center gap-3">
                          <img
                            data-testid="article-image-preview"
                            src={articleImagePreview}
                            alt="Preview"
                            className="w-32 h-20 object-cover border-2 border-black shadow-brutal-sm"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={clearArticleImage}
                            className="border-2 border-black hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                      )}
                    </div>
                    {articleImageError && (
                      <p className="text-red-600 text-sm font-medium mt-2">{articleImageError}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Content *
                    </label>
                    <Textarea
                      placeholder="Write your article content here..."
                      value={articleForm.content}
                      onChange={(e) => setArticleForm({...articleForm, content: e.target.value})}
                      className="min-h-[300px] border-2 border-black"
                    />
                  </div>

                  {articleMessage.text && (
                    <p className={`text-sm font-medium ${articleMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {articleMessage.text}
                    </p>
                  )}

                  <Button
                    type="submit"
                    disabled={articleSubmitting}
                    className="h-14 px-8 bg-[#BF0A30] hover:bg-[#9a0826] text-white font-bold uppercase tracking-wide border-2 border-black shadow-brutal"
                  >
                    {articleSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Plus className="w-5 h-5 mr-2" />
                    )}
                    Publish Article
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Free Agency Tab */}
          <TabsContent value="freeagency" className="space-y-6">
            {/* Auto-Fetch News Section */}
            <Card className="border-2 border-[#FFB612] shadow-brutal bg-gradient-to-r from-[#FFB612]/10 to-white">
              <CardHeader className="bg-[#FFB612] text-[#5A1414]">
                <CardTitle className="font-heading text-xl uppercase tracking-wide flex items-center gap-2">
                  <Rss className="w-5 h-5" />
                  Live News Feed
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="text-slate-700 mb-2">
                      <strong>Auto-importing</strong> Commanders transactions from ESPN and official team sources.
                    </p>
                    <p className="text-sm text-slate-500">
                      News is automatically fetched every 15 minutes. Click the button to fetch immediately.
                    </p>
                    {feedStatus && (
                      <div className="mt-3 flex flex-wrap gap-3 text-sm">
                        <span className="px-3 py-1 bg-green-100 text-green-700 font-medium rounded-full flex items-center gap-1">
                          <Zap className="w-3 h-3" />
                          {feedStatus.auto_imported} auto-imported
                        </span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 font-medium rounded-full">
                          {feedStatus.manually_added} manual
                        </span>
                        <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full">
                          {feedStatus.feeds_configured} feeds configured
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Button
                      data-testid="fetch-news-button"
                      onClick={handleFetchNews}
                      disabled={fetchingNews}
                      className="h-12 px-6 bg-[#5A1414] hover:bg-[#3d0d0d] text-white font-bold uppercase tracking-wide border-2 border-black shadow-brutal"
                    >
                      {fetchingNews ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Rss className="w-5 h-5 mr-2" />
                      )}
                      Fetch News Now
                    </Button>
                    {fetchMessage.text && (
                      <p className={`text-sm font-medium ${fetchMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                        {fetchMessage.text}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Add Deal Form */}
            <Card className="border-2 border-black shadow-brutal">
              <CardHeader className="bg-[#5A1414] text-white">
                <CardTitle className="font-heading text-xl uppercase tracking-wide flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add Free Agency Deal
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={handleDealSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Player Name *
                      </label>
                      <Input
                        data-testid="deal-player-name-input"
                        type="text"
                        placeholder="e.g., John Smith"
                        value={dealForm.player_name}
                        onChange={(e) => setDealForm({...dealForm, player_name: e.target.value})}
                        className="h-12 border-2 border-black"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Position *
                      </label>
                      <Input
                        data-testid="deal-position-input"
                        type="text"
                        placeholder="e.g., QB, WR, DE"
                        value={dealForm.position}
                        onChange={(e) => setDealForm({...dealForm, position: e.target.value})}
                        className="h-12 border-2 border-black"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Deal Type *
                      </label>
                      <Select 
                        value={dealForm.deal_type} 
                        onValueChange={(value) => setDealForm({...dealForm, deal_type: value})}
                      >
                        <SelectTrigger data-testid="deal-type-select" className="h-12 border-2 border-black">
                          <SelectValue placeholder="Select deal type" />
                        </SelectTrigger>
                        <SelectContent>
                          {dealTypeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              <span className="flex items-center gap-2">
                                <span 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: option.color }}
                                />
                                {option.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Details *
                    </label>
                    <Input
                      data-testid="deal-details-input"
                      type="text"
                      placeholder="e.g., Signed from Denver Broncos"
                      value={dealForm.details}
                      onChange={(e) => setDealForm({...dealForm, details: e.target.value})}
                      className="h-12 border-2 border-black"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Contract Value (optional)
                      </label>
                      <Input
                        data-testid="deal-contract-value-input"
                        type="text"
                        placeholder="e.g., $50M"
                        value={dealForm.contract_value}
                        onChange={(e) => setDealForm({...dealForm, contract_value: e.target.value})}
                        className="h-12 border-2 border-black"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Contract Years (optional)
                      </label>
                      <Input
                        data-testid="deal-contract-years-input"
                        type="text"
                        placeholder="e.g., 3 years"
                        value={dealForm.contract_years}
                        onChange={(e) => setDealForm({...dealForm, contract_years: e.target.value})}
                        className="h-12 border-2 border-black"
                      />
                    </div>
                  </div>

                  {dealMessage.text && (
                    <p className={`text-sm font-medium ${dealMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {dealMessage.text}
                    </p>
                  )}

                  <Button
                    data-testid="add-deal-submit-button"
                    type="submit"
                    disabled={dealSubmitting}
                    className="h-14 px-8 bg-[#5A1414] hover:bg-[#3d0d0d] text-white font-bold uppercase tracking-wide border-2 border-black shadow-brutal"
                  >
                    {dealSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Plus className="w-5 h-5 mr-2" />
                    )}
                    Add Deal
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Deals List */}
            <Card className="border-2 border-black shadow-brutal">
              <CardHeader className="bg-[#5A1414] text-white">
                <CardTitle className="font-heading text-xl uppercase tracking-wide flex items-center gap-2">
                  <FileSignature className="w-5 h-5" />
                  Commanders Free Agency Deals ({deals.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {deals.length === 0 ? (
                  <div className="text-center py-16">
                    <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No deals yet. Add your first free agency deal above!</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-100">
                        <TableHead className="font-bold text-[#5A1414]">Player</TableHead>
                        <TableHead className="font-bold text-[#5A1414]">Position</TableHead>
                        <TableHead className="font-bold text-[#5A1414]">Type</TableHead>
                        <TableHead className="font-bold text-[#5A1414]">Details</TableHead>
                        <TableHead className="font-bold text-[#5A1414]">Contract</TableHead>
                        <TableHead className="font-bold text-[#5A1414]">Source</TableHead>
                        <TableHead className="font-bold text-[#5A1414]">Date</TableHead>
                        <TableHead className="font-bold text-[#5A1414]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deals.map((deal) => {
                        const dealOption = dealTypeOptions.find(opt => opt.value === deal.deal_type) || dealTypeOptions[0];
                        return (
                          <TableRow key={deal.id} className="hover:bg-slate-50">
                            <TableCell className="font-medium">{deal.player_name}</TableCell>
                            <TableCell>{deal.position}</TableCell>
                            <TableCell>
                              <span 
                                className="px-2 py-1 text-xs font-bold uppercase rounded"
                                style={{ backgroundColor: `${dealOption.color}20`, color: dealOption.color }}
                              >
                                {dealOption.label}
                              </span>
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate">{deal.details}</TableCell>
                            <TableCell>
                              {deal.contract_value && <span className="block text-sm">{deal.contract_value}</span>}
                              {deal.contract_years && <span className="text-xs text-slate-500">{deal.contract_years}</span>}
                              {!deal.contract_value && !deal.contract_years && <span className="text-slate-400">-</span>}
                            </TableCell>
                            <TableCell>
                              {deal.auto_imported ? (
                                <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full flex items-center gap-1 w-fit">
                                  <Rss className="w-3 h-3" />
                                  Auto
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                                  Manual
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-slate-500">{formatDate(deal.date)}</TableCell>
                            <TableCell>
                              <Button
                                data-testid={`delete-deal-${deal.id}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDeal(deal.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scoreboard Tab - Manual Games for Commanders/Defenders */}
          <TabsContent value="scoreboard" className="space-y-6">
            <Card className="border-2 border-black shadow-brutal">
              <CardHeader className="bg-[#002868] text-white">
                <CardTitle className="font-heading text-xl uppercase tracking-wide flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add Commanders/Defenders Game
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-sm text-slate-600 mb-4">
                  Defenders (UFL) games are manually managed. MLB, NHL, NBA, MLS, and NWSL games are pulled automatically from their APIs.
                </p>
                <form onSubmit={handleScoreSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Team *</label>
                      <Select 
                        value={scoreForm.team} 
                        onValueChange={(value) => setScoreForm({...scoreForm, team: value})}
                      >
                        <SelectTrigger className="h-12 border-2 border-black">
                          <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="defenders">DC Defenders (UFL)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Game Date *</label>
                      <Input
                        type="date"
                        value={scoreForm.game_date}
                        onChange={(e) => setScoreForm({...scoreForm, game_date: e.target.value})}
                        className="h-12 border-2 border-black"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Game Time</label>
                      <Input
                        type="time"
                        value={scoreForm.game_time}
                        onChange={(e) => setScoreForm({...scoreForm, game_time: e.target.value})}
                        className="h-12 border-2 border-black"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Home Team *</label>
                      <Input
                        type="text"
                        placeholder="e.g., DC Defenders"
                        value={scoreForm.home_team}
                        onChange={(e) => setScoreForm({...scoreForm, home_team: e.target.value})}
                        className="h-12 border-2 border-black"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Away Team *</label>
                      <Input
                        type="text"
                        placeholder="e.g., Birmingham Stallions"
                        value={scoreForm.away_team}
                        onChange={(e) => setScoreForm({...scoreForm, away_team: e.target.value})}
                        className="h-12 border-2 border-black"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Home Score</label>
                      <Input
                        type="number"
                        placeholder="--"
                        value={scoreForm.home_score}
                        onChange={(e) => setScoreForm({...scoreForm, home_score: e.target.value})}
                        className="h-12 border-2 border-black"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Away Score</label>
                      <Input
                        type="number"
                        placeholder="--"
                        value={scoreForm.away_score}
                        onChange={(e) => setScoreForm({...scoreForm, away_score: e.target.value})}
                        className="h-12 border-2 border-black"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Status</label>
                      <Select 
                        value={scoreForm.status} 
                        onValueChange={(value) => setScoreForm({...scoreForm, status: value})}
                      >
                        <SelectTrigger className="h-12 border-2 border-black">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Scheduled">Scheduled</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="Final">Final</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">DC Team Is</label>
                      <Select 
                        value={scoreForm.is_home ? "home" : "away"} 
                        onValueChange={(value) => setScoreForm({...scoreForm, is_home: value === "home"})}
                      >
                        <SelectTrigger className="h-12 border-2 border-black">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="home">Home</SelectItem>
                          <SelectItem value="away">Away</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {scoreMessage.text && (
                    <p className={`text-sm font-medium ${scoreMessage.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                      {scoreMessage.text}
                    </p>
                  )}

                  <Button
                    type="submit"
                    disabled={scoreSubmitting}
                    className="h-12 px-6 bg-[#002868] hover:bg-[#001845] text-white font-bold uppercase tracking-wide border-2 border-black shadow-brutal"
                  >
                    {scoreSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Plus className="w-5 h-5 mr-2" />
                    )}
                    Add Game
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Games List - Only Defenders (UFL has no API) */}
            <Card className="border-2 border-[#C8102E] shadow-brutal">
              <CardHeader className="bg-[#C8102E] text-white">
                <CardTitle className="font-heading text-lg uppercase tracking-wide">
                  Defenders Games ({manualScores.defenders?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {(!manualScores.defenders || manualScores.defenders.length === 0) ? (
                  <div className="text-center py-8">
                    <Trophy className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">No games added yet</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {manualScores.defenders.map((game) => (
                      <div key={game.id} className="p-3 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="text-sm font-medium">
                            {game.away_team} @ {game.home_team}
                          </div>
                          <div className="text-xs text-slate-500">
                            {game.game_date} {game.game_time && `• ${game.game_time}`} • {game.status}
                          </div>
                          {game.status === 'Final' && (
                            <div className="text-sm font-bold mt-1">
                              {game.away_score} - {game.home_score}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteScore(game.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
